import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useTransactionStore } from '@/store/useTransactionStore'

import { BaseView, BaseText } from '@/components/base'
import { StickyHeaderNavigation, FixedBottomButton } from '@/components'
import useOffsets from '@/hooks/useOffsets'

import { useTelegramViewport } from './hooks/useTelegramViewport'
import { usePaymentData } from './hooks/usePaymentData'
import { LoadingScreen } from './components/LoadingScreen'
import { PaymentIframe } from './components/PaymentIframe'
import { CheckoutSection } from './components/CheckoutSection'

export default function OnRampWebview() {
  const { t } = useTranslation()
  const offsets = useOffsets()
  const navigate = useNavigate()
  const clearLastTransactionData = useTransactionStore(
    (state) => state.clearLastTransactionData,
  )

  // Initialize Telegram viewport
  useTelegramViewport()

  // Load payment data
  const {
    paymentUrl,
    paymentSessionToken,
    paymentSessionSecret,
    paymentSession,
    transactionId,
    isLoading,
  } = usePaymentData()

  const handleGoBack = useCallback(() => {
    clearLastTransactionData()
    navigate('/activity')
  }, [clearLastTransactionData, navigate])

  // Memoize container style to prevent re-renders
  const containerStyle = useMemo(
    () => ({
      position: 'fixed' as const,
      top: `${56 + offsets.top}px`,
      left: 0,
      right: 0,
      bottom: 0,
      overflow: 'auto' as const,
      WebkitOverflowScrolling: 'touch' as const,
      paddingBottom: offsets.bottom,
    }),
    [offsets.top, offsets.bottom],
  )

  if (isLoading) {
    return <LoadingScreen />
  }

  return (
    <BaseView flex={1} bgColor="background.screen">
      {/* Header */}
      <StickyHeaderNavigation
        title={t('common:payment')}
        onBackPress={handleGoBack}
      />

      {/* Content */}
      <BaseView style={containerStyle}>
        {/* Iframe for direct payment URL */}
        {paymentUrl && (
          <PaymentIframe
            paymentUrl={paymentUrl}
            transactionId={transactionId}
          />
        )}

        {/* Checkout Flow for Checkout.com */}
        {!paymentUrl && paymentSessionToken && (
          <CheckoutSection
            paymentSessionToken={paymentSessionToken}
            paymentSessionSecret={paymentSessionSecret}
            paymentSession={paymentSession}
            transactionId={transactionId}
          />
        )}

        {/* Bottom Section */}
        <BaseView
          style={{
            backgroundColor: '#131415',
            borderTop: '1px solid #2A2D31',
          }}
        >
          <BaseView px={16} pt={12} pb={12}>
            <BaseView mb={12}>
              <BaseText
                fontSize={14}
                color="label.secondary"
                textAlign="center"
              >
                {t('common:followInstructionsToCompletePayment')}
              </BaseText>
            </BaseView>
          </BaseView>

          <BaseView px={16} pb={16}>
            <FixedBottomButton
              buttons={{
                label: t('form:actions.cancel'),
                onPress: handleGoBack,
                type: 'secondaryGray',
              }}
              showBorder={false}
              zIndex={999}
              containerStyle={{
                position: 'static',
                backgroundColor: 'transparent',
                paddingBottom: 0,
              }}
            />
          </BaseView>
        </BaseView>
      </BaseView>
    </BaseView>
  )
}
